import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom"
import { AuthProvider, RequireAuth } from "@/auth/AuthProvider"
import { MainLayout } from "@/components/layout/MainLayout"
import { ToastProvider } from "@/lib/toast"
import { LoginPage } from "@/pages/LoginPage"
import { ModulStubPage } from "@/pages/ModulStubPage"
import { NotFoundPage } from "@/pages/NotFoundPage"
import { DashboardPage } from "@/pages/dashboard/DashboardPage"
import { AnlagenPage } from "@/pages/maschinen/AnlagenPage"
import { StrukturPage } from "@/pages/maschinen/StrukturPage"
import { AnlageDetailPage } from "@/pages/maschinen/AnlageDetailPage"
import { ObjektDetailPage } from "@/pages/maschinen/ObjektDetailPage"
import { KomponentenPage } from "@/pages/maschinen/KomponentenPage"
import { KomponenteDetailPage } from "@/pages/maschinen/KomponenteDetailPage"
import { PruefungenPage } from "@/pages/maschinen/PruefungenPage"
import { PruefungDetailPage } from "@/pages/maschinen/PruefungDetailPage"
import { WartungenPage } from "@/pages/maschinen/WartungenPage"
import { WartungDetailPage } from "@/pages/maschinen/WartungDetailPage"
import { ErsatzteilePage } from "@/pages/maschinen/ErsatzteilePage"
import { ErsatzteilDetailPage } from "@/pages/maschinen/ErsatzteilDetailPage"
import { ZeiterfassungPage } from "@/pages/timetab/ZeiterfassungPage"
import { ZeitbuchungenHistoriePage } from "@/pages/timetab/ZeitbuchungenHistoriePage"
import { TicketsPage } from "@/pages/tickets/TicketsPage"
import { TicketDetailPage } from "@/pages/tickets/TicketDetailPage"
import { EreignisPage } from "@/pages/ereignis/EreignisPage"
import { EreignisDetailPage } from "@/pages/ereignis/EreignisDetailPage"
import { EreignisDashboardPage } from "@/pages/ereignis/EreignisDashboardPage"
import { KvpBoardPage } from "@/pages/kvp/KvpBoardPage"
import { KvpAufgabeDetailPage } from "@/pages/kvp/KvpAufgabeDetailPage"
import { StammdatenPage } from "@/pages/stammdaten/StammdatenPage"

const queryClient = new QueryClient()

// Die 5 Module des Technik-Programmteils (ARCHITEKTUR.md §1/E1) — Ziele sind
// jetzt Sidebar-(Unter-)einträge (components/layout/Sidebar.tsx), diese Liste
// bleibt nur noch für den ModulStubPage-Fallback (Module ohne eigene Seite).
export const MODULE = [
  { pfad: "maschinen", titel: "Maschinen", untertitel: "Anlagen, Prüfungen, Wartungen, Ersatzteile (Ninox 02)" },
  { pfad: "timetab", titel: "TimeTab", untertitel: "Zeiterfassung + Abrechnungs-Exporte (Ninox 03)" },
  { pfad: "tickets", titel: "Servicetickets", untertitel: "Ticket-Hub mit polymorpher Herkunft (Ninox 09)" },
  { pfad: "ereignis", titel: "Ereignismeldung", untertitel: "Mängel, Störungen, Missstände (Ninox 10)" },
  { pfad: "kvp", titel: "KVP", untertitel: "Projekte, Teilprojekte, Aufgaben (Ninox 05)" },
] as const

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <BrowserRouter basename={import.meta.env.BASE_URL.replace(/\/$/, "")}>
          {/* AuthProvider muss innerhalb des Routers stehen (LogtoAuth nutzt
              useLocation/useNavigate fuer den /callback-Redirect-Flow). */}
          <AuthProvider>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route
                element={
                  <RequireAuth>
                    <MainLayout />
                  </RequireAuth>
                }
              >
                {/* Login/OIDC landet auf "/" — ohne Redirect griffe path="*" (404) */}
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/maschinen" element={<AnlagenPage />} />
                <Route path="/maschinen/struktur" element={<StrukturPage />} />
                <Route path="/maschinen/anlagen/:id" element={<AnlageDetailPage />} />
                <Route path="/maschinen/objekte/:id" element={<ObjektDetailPage />} />
                <Route path="/maschinen/komponenten" element={<KomponentenPage />} />
                <Route path="/maschinen/komponenten/:id" element={<KomponenteDetailPage />} />
                <Route path="/maschinen/pruefungen" element={<PruefungenPage />} />
                <Route path="/maschinen/pruefungen/:id" element={<PruefungDetailPage />} />
                <Route path="/maschinen/wartungen" element={<WartungenPage />} />
                <Route path="/maschinen/wartungen/:id" element={<WartungDetailPage />} />
                <Route path="/maschinen/ersatzteile" element={<ErsatzteilePage />} />
                <Route path="/maschinen/ersatzteile/:id" element={<ErsatzteilDetailPage />} />
                <Route path="/timetab" element={<ZeiterfassungPage />} />
                <Route path="/timetab/historie" element={<ZeitbuchungenHistoriePage />} />
                <Route path="/tickets" element={<TicketsPage />} />
                <Route path="/tickets/:id" element={<TicketDetailPage />} />
                <Route path="/ereignis" element={<EreignisPage />} />
                <Route path="/ereignis/dashboard" element={<EreignisDashboardPage />} />
                <Route path="/ereignis/:id" element={<EreignisDetailPage />} />
                <Route path="/kvp" element={<KvpBoardPage />} />
                <Route path="/kvp/aufgaben/:id" element={<KvpAufgabeDetailPage />} />
                <Route path="/stammdaten" element={<StammdatenPage />} />
                {(MODULE as readonly { pfad: string; titel: string; untertitel: string }[])
                  .filter(
                    (m) =>
                      m.pfad !== "maschinen" &&
                      m.pfad !== "timetab" &&
                      m.pfad !== "tickets" &&
                      m.pfad !== "ereignis" &&
                      m.pfad !== "kvp"
                  )
                  .map((m) => (
                    <Route
                      key={m.pfad}
                      path={`/${m.pfad}`}
                      element={<ModulStubPage modul={m.pfad} titel={m.titel} untertitel={m.untertitel} />}
                    />
                  ))}
                <Route path="*" element={<NotFoundPage />} />
              </Route>
            </Routes>
          </AuthProvider>
        </BrowserRouter>
      </ToastProvider>
    </QueryClientProvider>
  )
}
